<script setup lang="ts">
import { onClickOutside, onKeyStroke } from '@vueuse/core';
import CssIcon from './CssIcon.vue';
import type { MenuItem } from '~/composables/useMobileMenu';

const props = defineProps<{
  button: MenuItem;
  disabled?: boolean;
}>();

const route = useRoute();
const layoutStore = useLayoutStore();

const isOpen = ref(false);
const wrapper = ref<HTMLElement | null>(null);

const options = [
  {
    key: 'cassino',
    text: 'Cassino',
    to: '/games/category/all?initialCategory=jogos-ao-vivo',
  },
  {
    key: 'esporte',
    text: 'Esporte',
    to: '/sports',
  },
];

// Qual das duas opções corresponde à rota atual (pinta só ela).
const activeOptionKey = computed(() => {
  if (route.path.startsWith('/sports')) {
    return 'esporte';
  }

  if (route.query.initialCategory === 'jogos-ao-vivo') {
    return 'cassino';
  }

  return null;
});

function toggle() {
  if (props.disabled) {
    return;
  }

  isOpen.value = !isOpen.value;
}

function selectOption(to: string) {
  isOpen.value = false;
  layoutStore.closeMenu();
  navigateTo(to);
}

onClickOutside(wrapper, () => {
  isOpen.value = false;
});

onKeyStroke('Escape', () => {
  isOpen.value = false;
});

// Fecha ao trocar de rota (inclusive quando a navegação parte de outro lugar).
watch(
  () => route.fullPath,
  () => {
    isOpen.value = false;
  },
);
</script>

<template>
  <div
    ref="wrapper"
    class="item-wrapper"
  >
    <button
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      class="btn"
      :class="{ active: props.button.activeCondition }"
      :disabled="props.disabled"
      type="button"
      @click="toggle"
    >
      <span
        v-if="props.button.hasBadge"
        class="badge-live"
      />
      <span class="icon-slot">
        <CssIcon :name="props.button.icon" />
      </span>
      <span class="label">{{ props.button.text }}</span>
    </button>

    <div
      v-if="isOpen"
      class="dropdown"
      role="menu"
    >
      <button
        v-for="option in options"
        :key="option.key"
        :aria-current="activeOptionKey === option.key ? 'page' : undefined"
        class="dropdown-item"
        :class="{ 'dropdown-item--active': activeOptionKey === option.key }"
        role="menuitem"
        type="button"
        @click="selectOption(option.to)"
      >
        <!-- Ícones exportados do Figma (node DROP-GAME): "roulette 2" numa caixa
             de 17.644px com a arte de 13.112x15.36 e "fi_6658491" de
             13.487x15.173. Tamanhos explícitos para preservar a geometria. -->
        <span
          v-if="option.key === 'cassino'"
          class="option-icon option-icon--cassino"
        >
          <svg
            aria-hidden="true"
            fill="none"
            viewBox="0 0 13.1123 15.3604"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.4082 13.9736C9.63572 14.1729 9.91912 14.3099 10.2314 14.3594L10.3262 14.3721V15.3604C9.744 15.3244 9.21256 15.0992 8.79395 14.7451L8.71191 14.6719L9.40039 13.9834L9.4082 13.9736ZM12.2881 14.6045C12.2968 14.6132 12.3062 14.6208 12.3164 14.627C11.9061 15.0237 11.3668 15.2877 10.7676 15.3506L10.6543 15.3604V14.3721C11.0255 14.3352 11.3605 14.1749 11.6172 13.9326L12.2881 14.6045ZM8.85547 12.9014C8.88291 13.1791 8.9796 13.4367 9.12793 13.6562L9.18652 13.7363L9.16797 13.751L8.48438 14.4355C8.15321 14.0446 7.9339 13.5549 7.87695 13.0176L7.86719 12.9023H8.84766L8.85547 12.9014ZM12.1328 12.9023H13.1123C13.0799 13.4264 12.8947 13.9092 12.5996 14.3057L12.5342 14.3887L12.5205 14.3721L11.833 13.6836C11.9921 13.4581 12.0954 13.1907 12.124 12.9014L12.1328 12.9023ZM0.129883 10.7461C0.190854 10.8398 0.269418 10.8963 0.354492 10.8965L8.12598 10.8955C7.72953 11.4037 7.49316 12.0438 7.49316 12.7383C7.49323 13.2842 7.63942 13.7965 7.89453 14.2373L2.18945 14.2363C1.56916 14.2363 0.934638 13.7039 0.720703 13.043L0.298828 11.7402L0.176758 11.3838L0.15625 11.3193C0.113569 11.1777 0.0913756 11.0635 0.09375 10.9512C0.095372 10.8763 0.107612 10.8074 0.129883 10.7461ZM10.4902 11.4238C11.2158 11.4239 11.8037 12.0128 11.8037 12.7383C11.8035 13.4636 11.2156 14.0517 10.4902 14.0518C9.76478 14.0518 9.17697 13.4637 9.17676 12.7383C9.17676 12.0127 9.76465 11.4238 10.4902 11.4238ZM9.16797 11.7246L9.18652 11.7393C9.02555 11.9491 8.9136 12.1993 8.86914 12.4717L8.85547 12.5742L8.84766 12.5732H7.86719C7.9032 11.9906 8.12977 11.4597 8.48438 11.041L9.16797 11.7246ZM12.4658 11.0059C12.8129 11.4015 13.0433 11.9022 13.1025 12.4541L13.1123 12.5732H12.1328L12.124 12.5742C12.0949 12.2795 11.9883 12.0073 11.8242 11.7793L11.7676 11.7051L12.4658 11.0059ZM10.3262 11.1035C9.97633 11.1382 9.65839 11.2826 9.40918 11.502L9.40527 11.4971L8.71191 10.8037C9.1422 10.4079 9.70509 10.1535 10.3262 10.1152V11.1035ZM10.6543 10.1152C11.2214 10.1502 11.7399 10.3657 12.1533 10.7041L12.2344 10.7725L11.5361 11.4717C11.316 11.2897 11.0475 11.1636 10.7529 11.1162L10.6533 11.1035L10.6543 11.0957V10.1152ZM11.7129 9.1748C11.8948 9.17493 12.0449 9.31061 12.0654 9.48535L12.0674 9.52637V10.0186C12.0673 10.0721 12.0549 10.1226 12.0332 10.168C11.5825 9.89656 11.0545 9.74124 10.4902 9.74121C9.79817 9.74121 9.16065 9.97519 8.65332 10.3691L0.354492 10.3701C0.172755 10.3699 0.022568 10.2341 0.00195312 10.0596L0 10.0186V9.52637C0 9.3461 0.136964 9.19707 0.313477 9.17676L0.354492 9.1748H11.7129ZM6.03418 0C6.56324 0.000230034 6.99219 0.425189 6.99219 0.949219C6.99213 1.36225 6.72547 1.71336 6.35352 1.84375L6.35254 2.52734H7.04785C7.1804 2.16064 7.53437 1.89852 7.9502 1.89844C8.47946 1.89844 8.9082 2.32348 8.9082 2.84766C8.90801 3.37166 8.47933 3.79688 7.9502 3.79688C7.53429 3.79679 7.18033 3.53382 7.04785 3.16699L6.35254 3.16602L6.35352 3.59277C6.53266 3.68221 6.66718 3.84455 6.71973 4.04102L7.95117 8.64844H4.11621L5.34766 4.04102C5.40168 3.83899 5.53928 3.68117 5.71387 3.59375V3.16602L5.01953 3.16699C4.88702 3.53384 4.53315 3.79686 4.11719 3.79688C3.58805 3.79688 3.15938 3.37166 3.15918 2.84766C3.15918 2.32348 3.58793 1.89844 4.11719 1.89844C4.53306 1.89845 4.88695 2.16061 5.01953 2.52734H5.71387V1.84375C5.34198 1.71332 5.07525 1.36221 5.0752 0.949219C5.0752 0.425047 5.50492 0 6.03418 0Z"
              fill="currentColor"
            />
          </svg>
        </span>

        <span
          v-else
          class="option-icon option-icon--esporte"
        >
          <svg
            aria-hidden="true"
            fill="none"
            viewBox="0 0 13.4872 15.1731"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.65099 13.4887L2.65099 7.88143L4.71742 7.88143C4.85993 8.88299 5.71135 9.65214 6.74365 9.65214C7.77595 9.65214 8.62737 8.88299 8.76989 7.88143L10.8363 7.88143L10.8363 13.4887L9.08232 13.4887L9.08232 11.718L9.08232 11.1277L8.49765 11.1277L4.98965 11.1277L4.40499 11.1277L4.40499 11.718L4.40499 13.4887L2.65099 13.4887ZM8.76989 7.2912C8.62737 6.28964 7.77595 5.5205 6.74365 5.5205C5.71135 5.5205 4.85993 6.28964 4.71742 7.2912L2.65099 7.2912L2.65099 1.68397L4.40499 1.68397L4.40499 3.45467L4.40499 4.04491L4.98965 4.04491L8.49765 4.04491L9.08232 4.04491L9.08232 3.45467L9.08232 1.68397L10.8363 1.68397L10.8363 7.2912L8.76989 7.2912ZM5.31122 7.2912C5.44642 6.61796 6.03657 6.11073 6.74365 6.11073C7.45073 6.11073 8.04088 6.61796 8.17608 7.2912L5.31122 7.2912ZM5.31122 7.88143L8.17608 7.88143C8.04088 8.55467 7.45073 9.0619 6.74365 9.0619C6.03657 9.0619 5.44642 8.55467 5.31122 7.88143ZM8.49765 11.718L8.49765 13.4887L4.98965 13.4887L4.98965 11.718L8.49765 11.718ZM4.98965 3.45467L4.98965 1.68397L8.49765 1.68397L8.49765 3.45467L4.98965 3.45467Z"
              fill="currentColor"
            />
          </svg>
        </span>

        <span class="dropdown-label">{{ option.text }}</span>
      </button>

      <span
        aria-hidden="true"
        class="arrow"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.item-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  flex: 1;
}

.btn {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: rgba(255, 255, 255, 1);
  min-width: 44px;
  height: 100%;

  &.active {
    color: #00c9ff;
  }

  &:disabled {
    opacity: 0.45;
    pointer-events: none;
  }
}

.icon-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
}

.label {
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
}

.badge-live {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff3400;
  animation: blink 1800ms linear infinite;
}

/*
  Popover ancorado acima do item. `transition` é resetado globalmente no
  main.scss, então a entrada usa @keyframes.
*/
.dropdown {
  position: absolute;
  bottom: calc(100% + 12px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 10px;
  border-radius: 5px;
  background: #0d1423;
  animation: dropdown-in 140ms ease-out both;
}

/* "Dropbutton" do Figma: 105x26, flat/default-flat, raio 6, px 11, gap 6 */
.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  width: 105px;
  height: 26px;
  padding: 0 11px;
  border-radius: 6px;
  color: #fff;
  background: var(--colors-flat-default-flat, rgba(63, 63, 70, 0.4));

  &:active {
    background: rgba(63, 63, 70, 0.8);
  }
}

.dropdown-item--active {
  color: #00c9ff;
}

.dropdown-label {
  font-size: 12px;
  font-weight: 600;
  line-height: 12px;
  letter-spacing: -0.24px;
  white-space: nowrap;
  word-break: break-word;
}

.option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: clip;

  svg {
    display: block;
  }
}

.option-icon--cassino {
  width: 17.644px;
  height: 17.644px;

  svg {
    width: 13.112px;
    height: 15.36px;
  }
}

.option-icon--esporte {
  width: 13.487px;
  height: 15.173px;

  svg {
    width: 100%;
    height: 100%;
  }
}

/*
  Rabinho: no Figma é o "Union", um quadrado de 15.07px arredondado e rotacionado
  45°, na mesma cor do card, transbordando ~6.4px pra baixo.
*/
.arrow {
  position: absolute;
  bottom: -6.4px;
  left: 50%;
  width: 15.07px;
  height: 15.07px;
  transform: translateX(-50%) rotate(45deg);
  border-radius: 4px;
  background: #0d1423;
  z-index: -1;
}

@keyframes dropdown-in {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}
</style>
